import { BaseInput } from "@/components/input/CustomInputs";
import MultiSelect from "@/components/input/MultiSelect";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FC, FormEvent, useEffect, useState } from "react";
import VoipTable from "../VoipTable";
import { toast } from "sonner";
import axiosInstance from "@/utils/axiosInstance";
import { Input } from "@/components/ui/input";
import { Integration, User } from "@/interface/entityInterface";

interface Props {
  id?: number;
  title: string;
}

const VoipDialog: FC<Props> = ({ id, title }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<
    { id: number; firstname: string; lastname: string }[]
  >([]);
  const [formProp, setFormProp] = useState({
    server_url: "",
    recording_url: "",
    grant_type: "",
    client_id: "",
    client_secret: "",
  });
  const [extensions, setExtensions] = useState<
    {
      id: number;
      firstname: string;
      lastname: string;
      extension_id?: number;
      password?: string;
    }[]
  >([]);
  const [value, setValue] = useState("");

  useEffect(() => {
    if (!id) return;
    const getIntegration = async () => {
      const { data: users } = await axiosInstance.get<User[]>("/users");
      const { data } = await axiosInstance.get<Integration>(
        `/integrations/${id}`,
      );
      const { value, json_value } = data;
      const {
        client_id,
        client_secret,
        server_url,
        grant_type,
        recording_url,
        extensions,
      } = json_value;
      const operatorsExt = users.filter(user => extensions.find((ext: any) => ext.user_id === user.id)).map(operator => {
        const {id, firstname, lastname} = operator
        return {id, firstname, lastname}
      })
      setValue(value || "");
      setFormProp({
        client_id: client_id || "",
        client_secret: client_secret || "",
        grant_type: grant_type || "",
        recording_url: recording_url || "",
        server_url: server_url || "",
      });
      setExtensions(extensions);
      setUsers(operatorsExt)
    };

    getIntegration();
  }, [id]);

  const submitKey = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (extensions.length === 0) {
        toast("Select at least one user");
        return;
      }
      const objToSend = {
        key: "freepbx",
        value,
        json_value: {
          ...formProp,
          extensions: extensions.map((item) => {
            const { id, password, extension_id } = item;
            return {
              user_id: id,
              extension_id,
              password,
            };
          }),
        },
      };
      if (!id) {
        await axiosInstance.post("/integrations", objToSend);
        toast("Succesfully integrated");
      } else {
        await axiosInstance.put(`/integrations/${id}`, objToSend);
        toast("New integration data updated");
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newExtension = users.map((user) => {
      const { id, firstname, lastname } = user;
      return { id, firstname, lastname };
    });

    setExtensions(newExtension);
  }, [users]);

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline">{title}</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Voip integration</DialogTitle>
        <form onSubmit={submitKey}>
          <div className="grid sm:grid-cols-2 gap-4">
            <BaseInput
              id="server_url"
              label="Server url"
              value={formProp.server_url}
              setProp={setFormProp}
              placeholder="Enter your server url"
              type="url"
            />
            <BaseInput
              id="recording_url"
              label="Recording url"
              value={formProp.recording_url}
              setProp={setFormProp}
              placeholder="Enter recording url"
            />
            <div className="w-full">
              <Label className="mb-2">Key</Label>
              <Input
                id="value"
                name="value"
                value={value}
                onChange={(e: FormEvent<HTMLInputElement>) =>
                  setValue(e.currentTarget.value)
                }
                placeholder="Enter your key"
                required
              />
            </div>
            <BaseInput
              id="grant_type"
              label="Permission"
              value={formProp.grant_type}
              setProp={setFormProp}
              placeholder="set permissions"
            />
            <BaseInput
              id="client_id"
              label="Client id"
              value={formProp.client_id}
              setProp={setFormProp}
              placeholder="set client id"
            />
            <BaseInput
              id="client_secret"
              label="Client secret"
              value={formProp.client_secret}
              setProp={setFormProp}
              placeholder="set client secret"
            />
            <div className="w-full">
              <Label className="mb-2">Users</Label>
              <MultiSelect
                resource="users"
                selected={users}
                setSelected={setUsers}
                dataProp="email"
                objectProps={["firstname", "lastname"]}
              />
            </div>
          </div>
          <VoipTable
            data={extensions}
            setData={setExtensions}
            leadProp=""
            columns={[]}
          />
          <div className="flex justify-end">
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save configuration"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VoipDialog;
